import {
  normalizeFlightNumber,
  type FlightDirection,
  type FlightLeg,
  type FlightPassenger,
} from "@produce/shared";
import type { FlightLegInput, FlightPassengerInput } from "@/hooks/useBriefingFlights";

// Translation layer between the flights dialog's form fields and the
// database shape. Pure and separately tested, because this is where the
// subtle bugs live: a `<input type="datetime-local">` speaks wall-clock
// strings with no timezone at all, while the database stores true
// instants plus the airport's offset.
//
// The rule, in one line: the form always shows LOCAL AIRPORT time, and
// the offset used to interpret it is the one stored on the leg (filled
// exactly by the lookup API) or, absent that, the producer's own.

export interface FlightFormValues {
  direction: FlightDirection;
  legOrder: number;
  flightNumber: string;
  airline: string;
  aircraft: string;
  departureAirport: string;
  departureAirportName: string;
  departureTerminal: string;
  departureCheckInDesk: string;
  /** "YYYY-MM-DDTHH:mm" in the departure airport's local time. */
  departureLocal: string;
  departureOffsetMinutes: number | null;
  arrivalAirport: string;
  arrivalAirportName: string;
  arrivalTerminal: string;
  /** "YYYY-MM-DDTHH:mm" in the arrival airport's local time. */
  arrivalLocal: string;
  arrivalOffsetMinutes: number | null;
  notes: string;
}

export interface PassengerFormValues {
  briefingCrewId: string;
  bookingReference: string;
  seat: string;
  baggage: string;
  specialBaggage: string;
}

/** The producer's own UTC offset right now, in minutes (CEST → 120). */
export function currentUtcOffsetMinutes(): number {
  return -new Date().getTimezoneOffset();
}

/** ISO instant → "YYYY-MM-DDTHH:mm" as read on the clock at that airport. */
export function isoToLocalInput(iso: string | null, offsetMinutes: number | null): string {
  if (!iso) return "";
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return "";
  const shifted = new Date(parsed + (offsetMinutes ?? currentUtcOffsetMinutes()) * 60_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}` +
    `T${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`
  );
}

/** "YYYY-MM-DDTHH:mm" read at an airport with the given offset → ISO instant. */
export function localInputToISO(value: string, offsetMinutes: number | null): string | null {
  if (!value) return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match.map(Number) as unknown as number[];
  const asUTC = Date.UTC(year, month - 1, day, hour, minute);
  if (!Number.isFinite(asUTC)) return null;
  const offset = offsetMinutes ?? currentUtcOffsetMinutes();
  return new Date(asUTC - offset * 60_000).toISOString();
}

/** The date part of a local datetime-input value, for the lookup call. */
export function lookupDateFromForm(values: FlightFormValues): string {
  return values.departureLocal.slice(0, 10);
}

export function emptyFlightForm(direction: FlightDirection, legOrder: number): FlightFormValues {
  return {
    direction,
    legOrder,
    flightNumber: "",
    airline: "",
    aircraft: "",
    departureAirport: "",
    departureAirportName: "",
    departureTerminal: "",
    departureCheckInDesk: "",
    departureLocal: "",
    departureOffsetMinutes: null,
    arrivalAirport: "",
    arrivalAirportName: "",
    arrivalTerminal: "",
    arrivalLocal: "",
    arrivalOffsetMinutes: null,
    notes: "",
  };
}

export function legToForm(leg: FlightLeg): FlightFormValues {
  return {
    direction: leg.direction,
    legOrder: leg.leg_order,
    flightNumber: leg.flight_number ?? "",
    airline: leg.airline ?? "",
    aircraft: leg.aircraft ?? "",
    departureAirport: leg.departure_airport ?? "",
    departureAirportName: leg.departure_airport_name ?? "",
    departureTerminal: leg.departure_terminal ?? "",
    departureCheckInDesk: leg.departure_check_in_desk ?? "",
    departureLocal: isoToLocalInput(leg.departure_at, leg.departure_utc_offset_minutes),
    departureOffsetMinutes: leg.departure_utc_offset_minutes,
    arrivalAirport: leg.arrival_airport ?? "",
    arrivalAirportName: leg.arrival_airport_name ?? "",
    arrivalTerminal: leg.arrival_terminal ?? "",
    arrivalLocal: isoToLocalInput(leg.arrival_at, leg.arrival_utc_offset_minutes),
    arrivalOffsetMinutes: leg.arrival_utc_offset_minutes,
    notes: leg.notes ?? "",
  };
}

function trimmedOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

export function formToLegInput(values: FlightFormValues): FlightLegInput {
  const departureAt = localInputToISO(values.departureLocal, values.departureOffsetMinutes);
  const arrivalAt = localInputToISO(values.arrivalLocal, values.arrivalOffsetMinutes);

  // Recompute the duration from the instants rather than trusting a
  // stale value from a previous lookup: the producer may have adjusted
  // a time by hand after prefilling.
  let duration: number | null = null;
  if (departureAt && arrivalAt) {
    const minutes = Math.round((Date.parse(arrivalAt) - Date.parse(departureAt)) / 60_000);
    if (Number.isFinite(minutes) && minutes >= 0) duration = minutes;
  }

  return {
    direction: values.direction,
    leg_order: values.legOrder,
    airline: trimmedOrNull(values.airline),
    flight_number: normalizeFlightNumber(values.flightNumber) || null,
    departure_airport: trimmedOrNull(values.departureAirport.toUpperCase()),
    departure_airport_name: trimmedOrNull(values.departureAirportName),
    departure_terminal: trimmedOrNull(values.departureTerminal),
    departure_check_in_desk: trimmedOrNull(values.departureCheckInDesk),
    departure_at: departureAt,
    arrival_airport: trimmedOrNull(values.arrivalAirport.toUpperCase()),
    arrival_airport_name: trimmedOrNull(values.arrivalAirportName),
    arrival_terminal: trimmedOrNull(values.arrivalTerminal),
    arrival_at: arrivalAt,
    // Only claim an offset once a time is actually set, so an empty
    // field doesn't get stamped with the producer's timezone.
    departure_utc_offset_minutes: departureAt
      ? (values.departureOffsetMinutes ?? currentUtcOffsetMinutes())
      : null,
    arrival_utc_offset_minutes: arrivalAt
      ? (values.arrivalOffsetMinutes ?? currentUtcOffsetMinutes())
      : null,
    duration_minutes: duration,
    aircraft: trimmedOrNull(values.aircraft),
    notes: trimmedOrNull(values.notes),
  };
}

/** The subset of a lookup result the form consumes. */
export interface LookupFlightResult {
  flight_number: string;
  airline: string | null;
  aircraft: string | null;
  departure_airport: string | null;
  departure_airport_name: string | null;
  departure_terminal: string | null;
  departure_check_in_desk: string | null;
  departure_at: string | null;
  arrival_airport: string | null;
  arrival_airport_name: string | null;
  arrival_terminal: string | null;
  arrival_at: string | null;
  departure_utc_offset_minutes: number | null;
  arrival_utc_offset_minutes: number | null;
  duration_minutes: number | null;
}

/**
 * Overlay a lookup result on the form.
 *
 * The API wins for every field it actually knows — that is the point of
 * pressing the button. Fields it returns as null keep whatever the
 * producer already typed, so a partial answer never wipes good manual
 * input. `notes` is never touched: it is the producer's own text.
 */
export function applyLookupResult(
  values: FlightFormValues,
  result: LookupFlightResult,
): FlightFormValues {
  return {
    ...values,
    flightNumber: result.flight_number || values.flightNumber,
    airline: result.airline ?? values.airline,
    aircraft: result.aircraft ?? values.aircraft,
    departureAirport: result.departure_airport ?? values.departureAirport,
    departureAirportName: result.departure_airport_name ?? values.departureAirportName,
    departureTerminal: result.departure_terminal ?? values.departureTerminal,
    departureCheckInDesk: result.departure_check_in_desk ?? values.departureCheckInDesk,
    departureOffsetMinutes: result.departure_utc_offset_minutes ?? values.departureOffsetMinutes,
    departureLocal: result.departure_at
      ? isoToLocalInput(result.departure_at, result.departure_utc_offset_minutes)
      : values.departureLocal,
    arrivalAirport: result.arrival_airport ?? values.arrivalAirport,
    arrivalAirportName: result.arrival_airport_name ?? values.arrivalAirportName,
    arrivalTerminal: result.arrival_terminal ?? values.arrivalTerminal,
    arrivalOffsetMinutes: result.arrival_utc_offset_minutes ?? values.arrivalOffsetMinutes,
    arrivalLocal: result.arrival_at
      ? isoToLocalInput(result.arrival_at, result.arrival_utc_offset_minutes)
      : values.arrivalLocal,
  };
}

export function passengerToForm(
  briefingCrewId: string,
  passenger: FlightPassenger | undefined,
): PassengerFormValues {
  return {
    briefingCrewId,
    bookingReference: passenger?.booking_reference ?? "",
    seat: passenger?.seat ?? "",
    baggage: passenger?.baggage ?? "",
    specialBaggage: passenger?.special_baggage ?? "",
  };
}

export function formToPassengerInput(values: PassengerFormValues): FlightPassengerInput {
  return {
    briefing_crew_id: values.briefingCrewId,
    booking_reference: trimmedOrNull(values.bookingReference),
    seat: trimmedOrNull(values.seat),
    baggage: trimmedOrNull(values.baggage),
    special_baggage: trimmedOrNull(values.specialBaggage),
  };
}

/**
 * Copy the baggage allowance of the first passenger to everyone else on
 * the leg — the "same for everyone" shortcut. Baggage is usually
 * identical across a group booking, while booking reference and seat
 * are not and are deliberately left alone.
 */
export function applyBaggageToAll(passengers: PassengerFormValues[]): PassengerFormValues[] {
  const [first] = passengers;
  if (!first) return passengers;
  return passengers.map((p) => ({
    ...p,
    baggage: first.baggage,
    specialBaggage: first.specialBaggage,
  }));
}

/**
 * What the producer is told after pressing "Look up".
 *
 * Every outcome ends in a usable instruction, because manual entry is
 * always available: a missing API key, an exhausted quota and an unknown
 * flight are all "fill it in by hand", not failures the producer has to
 * decode.
 */
export function lookupMessage(code: string | undefined, count: number): string {
  if (code === "not_configured") {
    return "Flight lookup isn't set up yet — fill the details in by hand.";
  }
  if (code === "rate_limited") {
    return "Flight lookup limit reached for now — fill the details in by hand.";
  }
  if (code === "not_subscribed") {
    return "Flight lookup isn't activated for this API key — fill the details in by hand.";
  }
  if (code === "not_found" || count === 0) {
    return "No schedule found for this flight and date — fill the details in by hand.";
  }
  if (count > 1) return `Found ${count} flights — filled in the first one.`;
  return "Flight details filled in.";
}

// ── Draft reconciliation ─────────────────────────────────────────────

/**
 * One leg being edited.
 *
 * `id` is assigned by the client before the first save (a UUID), so a
 * draft has a stable identity from the moment it is created. That is
 * what makes saving idempotent — two saves that overlap converge on one
 * row instead of inserting two.
 */
export interface FlightDraft {
  id: string;
  /** Touched since the last successful save. */
  dirty: boolean;
  values: FlightFormValues;
  passengers: PassengerFormValues[];
}

/**
 * Merge the server's flight rows into the drafts currently on screen.
 *
 * Two rules, both about not throwing away the producer's work:
 *   * a draft they have TOUCHED is never overwritten — React Query
 *     refetches on focus and after every save, and re-seeding mid-typing
 *     would silently revert what they were entering;
 *   * a draft the server doesn't know yet is kept — it is either not
 *     saved at all, or saved with the refetch still in flight. Those two
 *     are indistinguishable from here, and dropping the second would
 *     make a leg blink out of the dialog right after it was saved.
 *
 * Everything else is re-seeded from the server. Deleting a leg is
 * therefore the dialog's job (it removes the draft as well as the row),
 * not something this function infers from an absence.
 */
export function reconcileDrafts(
  current: FlightDraft[],
  legs: FlightLeg[],
  passengers: FlightPassenger[],
): FlightDraft[] {
  const onServer = new Set(legs.map((leg) => leg.id));

  const seeded = legs.map((leg) => {
    const existing = current.find((d) => d.id === leg.id);
    if (existing?.dirty) return existing;
    return {
      id: leg.id,
      dirty: false,
      values: legToForm(leg),
      passengers: passengers
        .filter((p) => p.flight_id === leg.id)
        .map((p) => passengerToForm(p.briefing_crew_id, p)),
    };
  });

  return [...seeded, ...current.filter((d) => !onServer.has(d.id))];
}

/** Drafts of one direction, in travel order. */
export function draftsForDirection(
  drafts: FlightDraft[],
  direction: FlightDirection,
): FlightDraft[] {
  return drafts
    .filter((d) => d.values.direction === direction)
    .sort((a, b) => a.values.legOrder - b.values.legOrder);
}

// ── Copying a passenger list between directions ──────────────────────

/**
 * Everyone travelling in one direction, deduplicated across its legs.
 *
 * The union rather than the first leg only: on a journey with a
 * stopover a crew member may join at the connection, and they belong in
 * "who is travelling" just as much.
 */
export function passengersForDirection(
  drafts: FlightDraft[],
  direction: FlightDirection,
): PassengerFormValues[] {
  const byCrewId = new Map<string, PassengerFormValues>();
  for (const draft of draftsForDirection(drafts, direction)) {
    for (const passenger of draft.passengers) {
      if (!byCrewId.has(passenger.briefingCrewId))
        byCrewId.set(passenger.briefingCrewId, passenger);
    }
  }
  return [...byCrewId.values()];
}

/**
 * Take the outbound passenger list over onto a return leg.
 *
 * Nine times out of ten the same people fly home, so re-ticking them
 * and retyping their booking references is pure duplication. What is
 * copied:
 *
 *   * the people — anyone missing is added;
 *   * booking reference and baggage — a return is normally the same
 *     booking under the same allowance;
 *   * NOT the seat, which is assigned per flight. Copying it would put
 *     a confidently wrong seat number in front of a crew member, which
 *     is worse than an empty field.
 *
 * Never overwrites: someone already on the leg keeps everything they
 * have, and only their still-empty fields are filled. Producers do
 * press buttons to see what they do, and this one must not be able to
 * eat work.
 */
export function copyPassengersFrom(
  source: PassengerFormValues[],
  current: PassengerFormValues[],
): PassengerFormValues[] {
  const byCrewId = new Map(current.map((p) => [p.briefingCrewId, p]));

  for (const from of source) {
    const existing = byCrewId.get(from.briefingCrewId);
    if (!existing) {
      byCrewId.set(from.briefingCrewId, { ...from, seat: "" });
      continue;
    }
    byCrewId.set(from.briefingCrewId, {
      ...existing,
      bookingReference: existing.bookingReference || from.bookingReference,
      baggage: existing.baggage || from.baggage,
      specialBaggage: existing.specialBaggage || from.specialBaggage,
    });
  }

  return [...byCrewId.values()];
}
